import { motion } from 'motion/react';

import { useNavigation } from '@hooks/useNavigation';
import { useUI } from '@hooks/useUI';

export const SideMenu = () => {
  const { showSideMenu, toggleSideMenu } = useUI();
  const { currentPage, navigate } = useNavigation();

  const menuItems = [
    { label: 'Top Trades', page: 'optionshome' },
    { label: 'BTC Market', page: 'btcmarket' },
    { label: 'ETH Market', page: 'ethmarket' },
    { label: 'SOL Market', page: 'solmarket' },
    // { label: 'XRP Market', page: 'xrpmarket' },
    { label: 'My Positions', page: 'positions' },
    { label: 'Settings', page: 'settings' },
    { label: 'Profile', page: 'profile' },
    { label: 'Logout', page: 'logout' },
  ] as const;

  const handleMenuItemClick = (item: (typeof menuItems)[number]) => {
    if (item.page !== 'logout') {
      navigate(item.page);
    } else {
      // TODO: Implement logout
      // For now, just close the menu
    }
    toggleSideMenu();
  };

  return (
    <>
      {/* Subtle Backdrop Dim */}
      <motion.div
        className="fixed inset-0 bg-brand-dark/20 backdrop-blur-sm z-40"
        initial={{ opacity: 0 }}
        animate={{ opacity: showSideMenu ? 1 : 0 }}
        transition={{ duration: 0.2, ease: 'easeOut' }}
        style={{ pointerEvents: showSideMenu ? 'auto' : 'none' }}
        onClick={toggleSideMenu}
      />

      {/* Side Menu */}
      <motion.div
        className="fixed top-0 left-0 h-full w-80 bg-brand-dark/40 backdrop-blur-xl border-r border-white/10 z-50 flex flex-col shadow-2xl"
        initial={{ x: '-100%' }}
        animate={{ x: showSideMenu ? 0 : '-100%' }}
        transition={{ duration: 0.25, ease: 'easeOut' }}
        style={{ pointerEvents: showSideMenu ? 'auto' : 'none' }}
      >
        {/* Glassmorphism overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-white/5 via-white/3 to-transparent pointer-events-none" />

        {/* Menu Header */}
        <div className="relative p-6 border-b border-white/10">
          <div className="flex items-center justify-between">
            <h2 className="font-['Inter:Semi_Bold',_sans-serif] font-semibold text-white text-[24px] drop-shadow-lg">
              MENU
            </h2>
            <motion.button
              onClick={toggleSideMenu}
              className="p-2 rounded-full hover:bg-white/10 transition-all duration-75 group"
              whileHover={{ rotate: 90 }}
              whileTap={{ scale: 0.95 }}
            >
              <div className="w-6 h-6 text-white flex items-center justify-center">
                <svg
                  className="w-6 h-6 transition-transform duration-75 group-hover:rotate-90"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </div>
            </motion.button>
          </div>
        </div>

        {/* Menu Items */}
        <div className="flex-1 p-4 relative">
          <nav className="space-y-3">
            {menuItems.map((item, index) => {
              const isActive = item.page === currentPage;
              return (
                <motion.button
                  key={item.label}
                  onClick={() => handleMenuItemClick(item)}
                  className={`w-full flex items-center justify-start p-4 rounded-xl transition-all duration-75 text-left group relative overflow-hidden ${
                    isActive
                      ? 'border border-brand-green/40 bg-gradient-to-r from-brand-green/20 to-brand-green/10 shadow-[0_0_20px_rgb(65_255_65/0.2)]'
                      : 'hover:bg-white/10'
                  }`}
                  whileTap={{ scale: 0.98 }}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{
                    opacity: showSideMenu ? 1 : 0,
                    x: showSideMenu ? 0 : -20,
                  }}
                  transition={{
                    delay: showSideMenu ? index * 0.03 : 0,
                    duration: 0.2,
                    ease: 'easeOut',
                  }}
                >
                  {/* Active state background glow */}
                  {isActive && (
                    <motion.div
                      className="absolute inset-0 bg-gradient-to-r from-brand-green/15 via-brand-green/8 to-transparent"
                      animate={{ opacity: [0.3, 0.6, 0.3] }}
                      transition={{
                        duration: 2.5,
                        repeat: Infinity,
                        ease: 'easeInOut',
                      }}
                    />
                  )}

                  {/* Active state left border accent */}
                  {isActive && (
                    <motion.div
                      className="absolute left-0 top-1/2 -translate-y-1/2 w-1 rounded-r-full bg-gradient-to-b from-brand-green to-brand-green/80 shadow-[0_0_20px_rgb(65_255_65/0.5)]"
                      animate={{
                        height: ['60%', '85%', '60%'],
                        opacity: [0.7, 1, 0.7],
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        ease: 'easeInOut',
                      }}
                    />
                  )}

                  <span
                    className={`font-['Inter:Medium',_sans-serif] font-medium text-[18px] relative z-10 transition-colors duration-75 ${
                      isActive
                        ? 'text-brand-green drop-shadow-[0_0_8px_rgb(65_255_65/0.6)]'
                        : 'text-white/90 group-hover:text-white'
                    }`}
                  >
                    {item.label}
                  </span>
                </motion.button>
              );
            })}
          </nav>
        </div>

        {/* Menu Footer */}
        <div className="relative p-6 border-t border-white/10">
          <div className="text-center text-white/60 text-sm">
            <p className="font-['Inter:Light',_sans-serif]">
              Trading Platform v1.0
            </p>
            <div className="mt-2 w-16 h-px bg-gradient-to-r from-transparent via-white/30 to-transparent mx-auto" />
          </div>
        </div>
      </motion.div>
    </>
  );
};
